import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  formatSubmissionStatusForAgentProfile,
  isSubmissionAutofillComplete,
} from "@aam/shared";
import { AgentProfileProvisioning } from "../AgentProfileProvisioning";
import { AgentOperationsPanel } from "../AgentOperationsPanel";
import { AgentRequestsPanel } from "../AgentRequestsPanel";
import { CarrierChangeRequestsPanel } from "../CarrierChangeRequestsPanel";
import { ReferralLinksPanel } from "../ReferralLinksPanel";
import { AgentDetailSkeleton } from "../skeletons/AgentDetailSkeleton";
import { AgentClaimPanel } from "./AgentClaimPanel";
import { AgentDetailHeader } from "./AgentDetailHeader";
import { ContractsTable, CARRIER_VALUE_TO_IMO, PINNACLE_AVAILABLE_CARRIERS } from "./ContractsTable";
import { ProfileSectionsView } from "./profileHelpers";
import { API_BASE } from "./types";
import { useAgentDetail } from "./useAgentDetail";
import { SurelcOnboardingPanel } from "./SurelcOnboardingPanel";

interface StaffAgentDetailProps {
  viewerUserId?: string;
}

export function StaffAgentDetail({ viewerUserId }: StaffAgentDetailProps) {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { token, loading, error, setError, user, contracts, reload, patchUser } = useAgentDetail(agentId);
  const [saving, setSaving] = useState<string | null>(null);
  const [copiedCell, setCopiedCell] = useState<string | null>(null);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [revealingIds, setRevealingIds] = useState<Set<string>>(new Set());
  const [revealedValues, setRevealedValues] = useState<Map<string, string>>(new Map());

  if (loading) return <AgentDetailSkeleton />;
  if (!user) {
    return <div className="rounded-xl border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">Agent not found.</div>;
  }

  const capabilities = user.capabilities;
  const profileSubmission = user.profileSubmission ?? user.latestSubmission;
  const latestSubmission = user.latestSubmission;
  const existingCarrierValues = new Set(contracts.map((contract) => contract.carrier));
  const addableCarriers = PINNACLE_AVAILABLE_CARRIERS.filter((carrier) => !existingCarrierValues.has(carrier.value));

  async function copyCell(cellId: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedCell(cellId);
      window.setTimeout(() => setCopiedCell((current) => (current === cellId ? null : current)), 1200);
    } catch {
      setError("Unable to copy value to clipboard.");
    }
  }

  async function addContract(carrier: string) {
    if (!token || !agentId || !user?.latestSubmission) return;
    setSaving("add-contract");
    setError(null);
    try {
      const imo = CARRIER_VALUE_TO_IMO[carrier];
      if (!imo) throw new Error("Unknown carrier selected");
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/provider-contracts`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          submissionId: user.latestSubmission.id,
          carrier,
          imo,
          status: "PENDING",
          source: "manual",
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to add contract");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add contract");
    } finally {
      setSaving(null);
    }
  }

  async function deleteContract(contractId: string) {
    if (!token || !agentId) return;
    setSaving(contractId);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/provider-contracts/${contractId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to delete contract");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete contract");
    } finally {
      setSaving(null);
    }
  }

  async function updateStatus(contractId: string, nextStatus: string, comment: string) {
    if (!token || !agentId) return;
    setSaving(contractId);
    const useOverride = capabilities.canOverrideContractStatus;
    const url = `${API_BASE}/api/agents/${agentId}/provider-contracts/${contractId}${useOverride ? "/override" : ""}`;
    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus, notes: comment.trim() || undefined }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to update contract");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update contract");
    } finally {
      setSaving(null);
    }
  }

  async function pinContract(contractId: string, reason: string) {
    if (!token || !agentId) return;
    setSaving(contractId);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/provider-contracts/${contractId}/pin`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason.trim() || undefined }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to pin contract");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to pin contract");
    } finally {
      setSaving(null);
    }
  }

  async function unpinContract(contractId: string) {
    if (!token || !agentId) return;
    setSaving(contractId);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/provider-contracts/${contractId}/unpin`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to unpin contract");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to unpin contract");
    } finally {
      setSaving(null);
    }
  }

  async function downloadGeneratedFile(submissionId: string, type: "w9" | "eft") {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/forms/download/${submissionId}/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to download ${type.toUpperCase()} file`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = type === "w9" ? "W9-Form-Filled.pdf" : "EFT-Form-Filled.pdf";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to download file.");
    }
  }

  async function downloadUploadedFile(fileId: string, fileName: string) {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/uploads/${encodeURIComponent(fileId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download file");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName || "upload";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to download file.");
    }
  }

  async function revealField(fieldId: string) {
    if (revealedIds.has(fieldId)) {
      setRevealedIds((current) => {
        const next = new Set(current);
        next.delete(fieldId);
        return next;
      });
      return;
    }
    if (revealedValues.has(fieldId)) {
      setRevealedIds((current) => new Set(current).add(fieldId));
      return;
    }
    if (!token || !agentId || !profileSubmission) return;
    setRevealingIds((current) => new Set(current).add(fieldId));
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/agents/${agentId}/submissions/${profileSubmission.id}/reveal-field`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ fieldId }),
        }
      );
      const payload = await res.json();
      if (!res.ok || !payload.success) {
        throw new Error(payload.error || "Unable to reveal field");
      }
      setRevealedValues((current) => new Map(current).set(fieldId, String(payload.data.value)));
      setRevealedIds((current) => new Set(current).add(fieldId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to reveal field.");
    } finally {
      setRevealingIds((current) => {
        const next = new Set(current);
        next.delete(fieldId);
        return next;
      });
    }
  }

  return (
    <section className="space-y-4">
      <AgentDetailHeader
        displayName={user.displayName || user.email}
        email={user.email}
        shell="staff"
        upline={user.upline}
        canEditEmail={capabilities.canEditProfile}
        agentId={user.id}
        onEmailUpdated={(nextEmail) => patchUser({ email: nextEmail })}
      />

      {error && (
        <div className="rounded-xl border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">
          {error}
        </div>
      )}

      {capabilities.canEditProfile && (
        <AgentClaimPanel
          agentId={user.id}
          displayName={user.displayName || user.email}
          email={user.email}
          canLogin={user.canLogin}
          onClaimed={(nextEmail) => patchUser({ email: nextEmail, canLogin: true })}
        />
      )}

      {capabilities.canEditCommissionLevel && (
        <AgentProfileProvisioning
          agentId={user.id}
          initial={{ commissionLevel: user.commissionLevel ?? null }}
          compensationType={user.compensationType ?? null}
          applicationCommissionLevel={
            typeof profileSubmission?.formData?.requestedCommissionLevel === "string"
              ? profileSubmission.formData.requestedCommissionLevel
              : null
          }
        />
      )}

      {capabilities.canViewReferralLinks && (
        <ReferralLinksPanel
          ownerUserId={user.id}
          context="managed-agent"
        />
      )}

      {profileSubmission && (
        <section className="rounded-xl border border-accent/30 bg-accent/10 p-4 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-xs uppercase tracking-[0.25em] text-accent">Agent Profile</h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-content-muted">
                Registered {new Date(profileSubmission.createdAt).toLocaleString()} • {profileSubmission.imo.replace(/_/g, " ")}
              </span>
              {!isSubmissionAutofillComplete(profileSubmission.status) && (
                <span className="rounded-full bg-surface-base px-2.5 py-1 text-xs text-content-primary">
                  {formatSubmissionStatusForAgentProfile(profileSubmission.status)}
                </span>
              )}
              {capabilities.canViewSubmissions && (
                <Link
                  to={`/dashboard/submissions/${profileSubmission.id}`}
                  className="text-xs text-yellow-400 hover:text-yellow-300"
                >
                  open full submission
                </Link>
              )}
            </div>
          </div>
          <ProfileSectionsView
            formData={profileSubmission.formData ?? {}}
            carriers={profileSubmission.carriers}
            onCopy={copyCell}
            copiedId={copiedCell}
            onReveal={revealField}
            revealedIds={revealedIds}
            revealingIds={revealingIds}
            revealedValues={revealedValues}
            allowFileDownloads
            onDownloadFile={downloadUploadedFile}
          />
        </section>
      )}

      {profileSubmission && (profileSubmission.canGenerateW9 || profileSubmission.canGenerateEft) && (
        <section className="rounded-xl border border-border-default bg-surface-overlay p-4">
          <h2 className="text-lg font-semibold text-content-primary mb-3">Files</h2>
          <div className="flex flex-wrap gap-2">
            {profileSubmission.canGenerateW9 && (
              <button
                type="button"
                onClick={() => downloadGeneratedFile(profileSubmission.id, "w9")}
                className="inline-flex items-center rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-medium text-accent"
              >
                Download W9
              </button>
            )}
            {profileSubmission.canGenerateEft && (
              <button
                type="button"
                onClick={() => downloadGeneratedFile(profileSubmission.id, "eft")}
                className="inline-flex items-center rounded-lg border border-status-active-border bg-status-active-bg px-4 py-2 text-sm font-medium text-status-active-text"
              >
                Download EFT
              </button>
            )}
          </div>
        </section>
      )}

      {user.surelc ? (
        <SurelcOnboardingPanel
          surelc={user.surelc}
          agentId={agentId!}
          token={token}
          onChanged={reload}
        />
      ) : null}

      <ContractsTable
        contracts={contracts}
        capabilities={capabilities}
        latestSubmissionId={latestSubmission?.id}
        addableCarriers={addableCarriers}
        onAddContract={capabilities.canAddContract ? addContract : undefined}
        onDeleteContract={capabilities.canDeleteContract ? deleteContract : undefined}
        onUpdateStatus={capabilities.canUpdateContractStatus && !capabilities.canOverrideContractStatus ? updateStatus : undefined}
        onOverrideStatus={capabilities.canOverrideContractStatus ? updateStatus : undefined}
        onPinContract={capabilities.canPinContract ? pinContract : undefined}
        onUnpinContract={capabilities.canPinContract ? unpinContract : undefined}
        saving={saving}
      />

      {capabilities.canMergeAgents && agentId && (
        <AgentOperationsPanel
          agentId={agentId}
          displayName={user.displayName || user.email}
          email={user.email}
          upline={user.upline}
          archivedAt={user.archivedAt ?? null}
          onUpdated={reload}
          onArchived={() => navigate("/dashboard/agents")}
        />
      )}

      {agentId && (
        <CarrierChangeRequestsPanel
          agentId={agentId}
          user={user}
          contracts={contracts}
          canManage={capabilities.canUpdateContractStatus}
        />
      )}

      {agentId && (
        <AgentRequestsPanel
          agentId={agentId}
          canSubmit={viewerUserId === agentId || capabilities.canViewOperations}
        />
      )}
    </section>
  );
}
